import type { CanonicalName } from '../types/parse';
import type { RecipientDestination } from '../types/recipients';
import type { FundingMethod, PayoutMethod } from '../utils/catalog';
import { detectDestinationCountry } from '../utils/geo';

/**
 * The 3 MVP intents for Lola Send.
 *   send    - send money to family & friends
 *   request - generate a request link with destination preconfigured
 *   pay     - send without preset destination; share a link for recipient
 *             to complete delivery details (out of scope for the wizard
 *             itself — the link generation is its terminal state).
 */
export type Intent = 'send' | 'request' | 'pay';

/**
 * Lifecycle phases the wizard cycles through. `commit_error` is a recoverable
 * terminal-ish state the FSM parks in when minting the canonical command fails
 * (e.g. the binding quote expired, the catalog went stale, or a required
 * payment instrument is missing) — the user can retry or start over.
 */
export type Phase =
  | 'compose'
  | 'collect'
  | 'kyc'
  | 'committing'
  | 'commit_error'
  | 'done';

/** Sub-steps inside the Compose phase. */
export type ComposeStep = 'intent' | 'route' | 'funding' | 'payout';

/**
 * All possible compose steps. The actual sequence the user goes through is
 * intent-dependent — see `composeStepsFor()`.
 */
export const COMPOSE_STEPS: ComposeStep[] = [
  'intent',
  'route',
  'funding',
  'payout',
];

/**
 * Returns the active compose steps for a given intent. The funding/payout
 * steps are conditional:
 *
 * - `send`    → both: the user is the sender (picks funding) AND fully defines
 *                the destination (picks payout for the recipient).
 * - `request` → only `payout`: the user wants to be paid; how the sender
 *                funds the transfer is the sender's prerogative when they
 *                claim the link.
 * - `pay`     → only `funding`: the user picks how they pay; the recipient
 *                will choose their own payout when they claim the link.
 *
 * When `intent` is `null` (no selection yet) we return the full set so the
 * accordion shows all four steps as upcoming.
 */
export const composeStepsFor = (intent: Intent | null): ComposeStep[] => {
  if (intent === 'request') return ['intent', 'route', 'payout'];
  if (intent === 'pay') return ['intent', 'route', 'funding'];
  return ['intent', 'route', 'funding', 'payout'];
};

/** Next active compose step, or `null` if `current` is already the last one. */
export const nextComposeStep = (
  current: ComposeStep,
  intent: Intent | null,
): ComposeStep | null => {
  const steps = composeStepsFor(intent);
  const idx = steps.indexOf(current);
  if (idx === -1 || idx === steps.length - 1) return null;
  return steps[idx + 1];
};

export type AmountDirection = 'send' | 'receive';

export interface RouteState {
  fromCountry: string | null;
  toCountry: string | null;
  amount: string | null;
  direction: AmountDirection;
}

/** Catalog details that depend on the chosen payout method (no PII). */
export interface PayoutCatalogState {
  state?: string;
  payer?: string;
  carrier?: string;
}

export interface ComposeState {
  intent: Intent | null;
  route: RouteState;
  funding: FundingMethod | null;
  payout: PayoutMethod | null;
  payoutCatalog: PayoutCatalogState;
}

// ----------------------------------------------------------------------------
// Phase B (Collect) — PII captured per intent.
// ----------------------------------------------------------------------------

/** Sub-steps inside the Collect phase. */
export type CollectStep =
  | 'recipient_identity'
  | 'recipient_payout'
  | 'funding_data'
  | 'summary';

/**
 * Per-intent sequence of collect steps.
 *
 * - `send`    → name of the recipient, where to deliver, how to fund.
 * - `request` → user enters their own payout method only (KYC validates the
 *               identity later, so we don't re-capture name here).
 * - `pay`     → user enters how they pay; the recipient will fill the rest
 *               when they claim the shared link.
 *
 * `summary` is always last and acts as the confirmation gate before KYC.
 */
export const collectStepsFor = (intent: Intent | null): CollectStep[] => {
  if (intent === 'request') return ['recipient_payout', 'summary'];
  if (intent === 'pay') return ['recipient_identity', 'funding_data', 'summary'];
  return ['recipient_identity', 'recipient_payout', 'funding_data', 'summary'];
};

export const nextCollectStep = (
  current: CollectStep,
  intent: Intent | null,
): CollectStep | null => {
  const steps = collectStepsFor(intent);
  const idx = steps.indexOf(current);
  if (idx === -1 || idx === steps.length - 1) return null;
  return steps[idx + 1];
};

export const prevCollectStep = (
  current: CollectStep,
  intent: Intent | null,
): CollectStep | null => {
  const steps = collectStepsFor(intent);
  const idx = steps.indexOf(current);
  if (idx <= 0) return null;
  return steps[idx - 1];
};

/**
 * Identity captured for the recipient. The user types a single string — the
 * legal full name as it appears on the recipient's ID — and the step
 * normalizes it into the platform's `CanonicalName` structure via
 * `POST /widget/parse/name` (with a positional client-side fallback when the
 * endpoint is unavailable). Both representations are kept: `fullName` drives
 * the UI (summary, card labels) and `canonicalName` travels on the canonical
 * command at commit time (`recipient.name`).
 */
export interface RecipientIdentity {
  fullName: string;
  /** Structured name preserved from the parse endpoint (or fallback split). */
  canonicalName: CanonicalName | null;
}

/**
 * Payout details captured in Fase B. The shape is a discriminated union
 * keyed by the payout method picked in Fase A so each branch carries only
 * the fields it actually needs (CLABE for bank, state+payer for cash, etc.).
 */
/**
 * Push-to-card payout instrument already registered (tokenized) in the backend.
 * The picker loads these from
 * `GET /widget/links/:linkId/payment-methods/destination-cards`, so we only
 * carry the `paymentMethodId` (consumed by the canonical command as
 * `payoutPaymentMethodId`) plus display-only fields.
 */
export interface PayoutCardSaved {
  type: 'PUSH_TO_CARD';
  source: 'saved';
  paymentMethodId: string;
  brand: string;
  last4: string;
  /** `MM/YY` for display only. */
  cardExpiry: string;
  label?: string;
}

/**
 * Network / product capabilities frozen on the destination-card dry-run.
 * Risk intel stays server-side — the widget only keeps what it needs for UX.
 */
export interface PayoutCardCapabilities {
  pullEnabled?: boolean;
  pushDomestic?: boolean;
  pushInternational?: boolean;
  isFastFundsSupported?: boolean;
}

/**
 * Push-to-card payout instrument typed manually. Pre-validated on the payout
 * step (VGS tokenize + CVS dry-run, no CVV). Carries `numberToken` +
 * `cvsValidationId` so Summary prepare can promote + provision without a
 * second network validation. Raw PAN is cleared after Continue — only
 * `last4` remains for display until prepare converts this to `PayoutCardSaved`.
 */
export interface PayoutCardNew {
  type: 'PUSH_TO_CARD';
  source: 'new';
  /**
   * Raw PAN kept only while the form is open. Cleared after successful
   * pre-validate Continue — register reuses `numberToken` instead.
   */
  cardPan?: string;
  cardExpiry: string;
  /** Display + register last4 (survives after PAN is dropped). */
  last4: string;
  brand?: string;
  /** CVS dry-run validationId from the payout-step pre-validate. */
  cvsValidationId: string;
  /** Persistent VGS PAN alias — lets register skip a second VGS round-trip. */
  numberToken: string;
  /** Optional capability flags from the dry-run (e.g. pushInternational). */
  capabilities?: PayoutCardCapabilities;
  /** Recipient name forwarded as cardholder when known. */
  cardholderName?: string;
}

/**
 * Destination bank account typed manually. Registered as a stored instrument
 * at commit time (`registerBankPayoutInstrument`).
 */
export interface PayoutBankNew {
  type: 'BANK_ACCOUNT';
  source: 'new';
  bankId: string | null;
  /** Local account format — CLABE in MX (18 digits), CBU/CVU in AR, etc. */
  accountNumber: string;
}

/**
 * Destination bank account already registered against the user. Replayed from
 * the recipient book via `paymentMethodId` — no CLABE re-entry.
 */
export interface PayoutBankSaved {
  type: 'BANK_ACCOUNT';
  source: 'saved';
  paymentMethodId: string;
  /** Catalog payer code when known (display / quote join). */
  bankId: string | null;
  /** Masked last4 for summary display. */
  last4: string;
  bankName?: string;
}

export type PayoutDetails =
  | PayoutBankNew
  | PayoutBankSaved
  | {
      type: 'CASH_PICKUP';
      /** ISO 3166-2 bare subdivision code (e.g. `JAL`). */
      stateCode: string | null;
      payerId: string | null;
    }
  | PayoutCardSaved
  | PayoutCardNew
  | {
      type: 'MOBILE_WALLET';
      carrierId: string | null;
      phone: string;
    }
  | {
      type: 'HOME_DELIVERY';
      address: string;
    };

/**
 * Billing address captured alongside card funding. Required by the backend's
 * `POST /widget/links/:linkId/payment-methods/cards` endpoint (the card
 * processor validates against it), so the card funding form collects it before
 * the card can be registered.
 */
export interface BillingAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

/**
 * Card funding instrument already registered (tokenized) in the backend. The
 * picker in `FundingDataStep` loads these from
 * `GET /widget/links/:linkId/payment-methods`, so we only carry the
 * `paymentMethodId` (the instrument the canonical command consumes as
 * `fundingPaymentMethodId`) plus display-only fields. No PAN/CVV/billing —
 * those live server-side behind the token, mirroring the proven flow in
 * raas-widgets-flow-m2m.
 */
export interface FundingCardSaved {
  type: 'CARD';
  source: 'saved';
  paymentMethodId: string;
  brand: string;
  last4: string;
  /** `MM/YY` for display only. */
  expiry: string;
  cardholderName?: string;
}

/**
 * Card funding instrument typed manually by the user. Pre-validated on the
 * funding step (VGS tokenize + CVS dry-run). Carries the volatile VGS
 * `cvvToken` (never the raw CVV) plus `numberToken` so Summary prepare can
 * promote + provision without keeping the security code in wizard state.
 * Lazy rail prepare converts this to `FundingCardSaved`.
 */
export interface FundingCardNew {
  type: 'CARD';
  source: 'new';
  cardholderName: string;
  pan: string;
  expiry: string;
  billingAddress: BillingAddress;
  /**
   * CVS dry-run validationId from the funding-step pre-validate.
   * When set, commit promotes this validation instead of re-running network checks.
   */
  cvsValidationId: string;
  /**
   * Volatile VGS CVV alias from the funding-step tokenize. Used for register
   * promote and first rail provision — raw CVV never enters wizard state.
   */
  cvvToken: string;
  /**
   * Persistent VGS PAN alias from the same tokenize. Lets register skip a
   * second VGS round-trip.
   */
  numberToken: string;
}

/**
 * Funding details captured in Fase B. Discriminated by `type` so card-based
 * funding methods share one shape (DEBIT_CARD + CREDIT_CARD) and account-based
 * methods (ACH, RTP, BANK_BALANCE) share another. Card funding is further
 * split by `source` — a `saved` card already lives in the backend (selected
 * from the picker) while a `new` card was just typed and still needs to be
 * tokenized + registered.
 */
export type FundingDetails =
  | FundingCardSaved
  | FundingCardNew
  | {
      type: 'ACCOUNT';
      routing: string;
      account: string;
    }
  | {
      type: 'OTHER';
    };

export interface CollectState {
  recipientIdentity: RecipientIdentity | null;
  /**
   * Habitual payout destinations from the recipient book entry the user
   * picked in the identity step. Empty when the name was typed free-text.
   * The payout step filters these by `compose.payout` (sender intent) to
   * auto-skip or offer one-tap replay — never re-pins a mismatched method.
   */
  bookDestinations: RecipientDestination[];
  payoutDetails: PayoutDetails | null;
  fundingDetails: FundingDetails | null;
  /**
   * `capabilityKey` (exact SKU) of the pre-quote option the user selected when
   * picking their payer in the payout substep. This is the binding identifier
   * the signed quote is priced against — captured at selection time (read off
   * the pre-quote option) so the quote and pre-quote describe the same offer.
   * `null` until a payout payer is chosen.
   */
  selectedCapabilityKey: string | null;
}

export interface QuoteSnapshot {
  quoteId: string;
  exchangeRate: number;
  totalFee: number;
  sendAmount: number;
  receiveAmount: number;
  sendCurrency: string;
  receiveCurrency: string;
  expiresAt: string;
  /**
   * `true` for indicative pre-quotes (FX is live, not binding) and `false`
   * once the binding signed quote has been resolved. Drives the "estimated"
   * disclaimers in the UI.
   */
  estimated: boolean;
  /**
   * Signed JWT from the binding quote (`POST /widget/operations/quote`). Only
   * present on a non-estimated snapshot — it's the source of truth used to
   * create the operation. Absent on pre-quote snapshots.
   */
  signedQuote?: string;
}

/**
 * Result of the (mocked) commit. For `send` we get an operation id; for
 * `request` and `pay` we get a shareable URL the user copies/shares.
 */
export interface CommitResult {
  operationId: string | null;
  shareLink: string | null;
  /**
   * Id of the money request (solicitud) minted for a `request` intent. Lets the
   * home re-fetch the requests list so the new request shows immediately.
   */
  requestId?: string | null;
  /** Deferred payout origination id for `pay` intent. */
  originationId?: string | null;
}

export interface WizardContext {
  phase: Phase;
  /**
   * Currently expanded compose step. `null` means every step is collapsed —
   * used after the user picks a payout method (the last compose step) so the
   * accordion summarizes everything and visual focus moves to the
   * RecommendedCard's Get Started CTA.
   */
  openStep: ComposeStep | null;
  /** Active collect substep (Phase B uses a sequential mini-stepper). */
  openCollectStep: CollectStep | null;
  compose: ComposeState;
  collect: CollectState;
  quote: QuoteSnapshot | null;
  cip: { required: boolean | 'unknown'; ticketId?: string };
  result: CommitResult;
  /**
   * Backend error code (or message) captured when the canonical command
   * creation failed. Drives the copy shown in the `commit_error` phase.
   * `null` whenever a commit hasn't failed.
   */
  commitError: string | null;
}

/**
 * Default route picked when no explicit selection is made. Mirrors what
 * SendFlow shows as the visible default (US → AR) so the FSM's notion of
 * "completeness" matches the UI from the first paint.
 */
export const emptyComposeState: ComposeState = {
  intent: null,
  route: {
    fromCountry: 'US',
    // Destination defaults to the user's detected country (today simulated
    // as 'MX'). Real geo-IP detection lives in `utils/geo.ts`.
    toCountry: detectDestinationCountry(),
    amount: null,
    // The amount field is always denominated in the destination currency —
    // the recipient receives exactly what's typed and the sender's USD
    // equivalent is derived by the quote engine.
    direction: 'receive',
  },
  funding: null,
  payout: null,
  payoutCatalog: {},
};

export const emptyCollectState: CollectState = {
  recipientIdentity: null,
  bookDestinations: [],
  payoutDetails: null,
  fundingDetails: null,
  selectedCapabilityKey: null,
};

export const initialWizardContext: WizardContext = {
  phase: 'compose',
  openStep: 'intent',
  openCollectStep: null,
  compose: emptyComposeState,
  collect: emptyCollectState,
  quote: null,
  cip: { required: 'unknown' },
  result: { operationId: null, shareLink: null },
  commitError: null,
};

/**
 * True when every Fase A field that gates Get Started is filled. Funding /
 * payout requirements depend on intent — see `composeStepsFor()`.
 */
export const isComposeComplete = (compose: ComposeState): boolean => {
  const baseValid = Boolean(
    compose.intent &&
      compose.route.fromCountry &&
      compose.route.toCountry &&
      compose.route.amount &&
      parseFloat(compose.route.amount) > 0,
  );
  if (!baseValid) return false;

  const steps = composeStepsFor(compose.intent);
  if (steps.includes('funding') && !compose.funding) return false;
  if (steps.includes('payout') && !compose.payout) return false;
  return true;
};

/**
 * Initial template for `PayoutDetails` based on the payout method. Keeps
 * the discriminant correct from the very first render so downstream
 * components can rely on `details.type` instead of guarding with `??`.
 */
export const emptyPayoutDetailsFor = (
  payout: PayoutMethod | null,
): PayoutDetails | null => {
  if (!payout) return null;
  switch (payout) {
    case 'BANK_ACCOUNT':
      return {
        type: 'BANK_ACCOUNT',
        source: 'new',
        bankId: null,
        accountNumber: '',
      };
    case 'CASH_PICKUP':
      return { type: 'CASH_PICKUP', stateCode: null, payerId: null };
    case 'PUSH_TO_CARD':
      return {
        type: 'PUSH_TO_CARD',
        source: 'new',
        cardExpiry: '',
        last4: '',
        cvsValidationId: '',
        numberToken: '',
      };
    case 'MOBILE_WALLET':
      return { type: 'MOBILE_WALLET', carrierId: null, phone: '' };
    case 'HOME_DELIVERY':
      return { type: 'HOME_DELIVERY', address: '' };
  }
};

/**
 * Whether the user has filled enough payout details to consider the step
 * complete. Each variant has its own minimum bar — bank requires a known
 * bank + a non-empty account, cash requires both state and payer, etc.
 */
export const isPayoutDetailsComplete = (
  details: PayoutDetails | null,
  destCountry: string | null,
): boolean => {
  if (!details) return false;
  switch (details.type) {
    case 'BANK_ACCOUNT': {
      // A saved instrument is complete as soon as it has its backend id —
      // the account number already lives server-side behind the token.
      if (details.source === 'saved') return Boolean(details.paymentMethodId);
      if (!details.bankId) return false;
      // MX requires 18-digit CLABE; AR requires 22-digit CBU. For unknown
      // countries accept an ISO 13616-sized alphanumeric identifier.
      const accountNumber = details.accountNumber
        .replace(/\s/g, '')
        .toUpperCase();
      if (destCountry === 'MX') return /^\d{18}$/.test(accountNumber);
      if (destCountry === 'AR') return /^\d{22}$/.test(accountNumber);
      return /^[A-Z0-9]{1,34}$/.test(accountNumber);
    }
    case 'CASH_PICKUP':
      return Boolean(details.stateCode && details.payerId);
    case 'PUSH_TO_CARD':
      if (details.source === 'saved') return Boolean(details.paymentMethodId);
      // After Continue the card is pre-validated: tokens + validationId, no PAN.
      return (
        Boolean(details.cvsValidationId) &&
        Boolean(details.numberToken) &&
        details.last4.length === 4 &&
        details.cardExpiry.length >= 4
      );
    case 'MOBILE_WALLET':
      return Boolean(details.carrierId) && details.phone.length >= 8;
    case 'HOME_DELIVERY':
      return details.address.trim().length > 5;
  }
};

/** A blank billing address, used to seed the card funding form. */
export const emptyBillingAddress = (): BillingAddress => ({
  street1: '',
  street2: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'US',
});

/** True when the billing address has the fields the backend requires. */
export const isBillingAddressComplete = (
  address: BillingAddress | null | undefined,
): boolean => {
  if (!address) return false;
  const zipOk =
    address.country === 'US'
      ? /^\d{5}(-\d{4})?$/.test(address.zipCode.trim())
      : address.zipCode.trim().length >= 2;
  return (
    address.street1.trim().length > 2 &&
    address.city.trim().length > 1 &&
    address.state.trim().length === 2 &&
    address.country.trim().length === 2 &&
    zipOk
  );
};

/** True when funding details have enough info to proceed. */
export const isFundingDetailsComplete = (
  details: FundingDetails | null,
): boolean => {
  if (!details) return false;
  switch (details.type) {
    case 'CARD':
      // A saved card is complete as soon as it has its backend instrument id —
      // PAN/CVV/billing already live server-side behind the token.
      if (details.source === 'saved') return Boolean(details.paymentMethodId);
      return (
        details.cardholderName.trim().length > 1 &&
        details.pan.replace(/\s/g, '').length >= 13 &&
        details.expiry.length >= 4 &&
        Boolean(details.cvvToken) &&
        Boolean(details.cvsValidationId) &&
        isBillingAddressComplete(details.billingAddress)
      );
    case 'ACCOUNT':
      return details.routing.length >= 6 && details.account.length >= 5;
    case 'OTHER':
      return true;
  }
};

/**
 * True when every collect substep relevant to the current intent has data.
 * Used by the summary screen to gate the "confirm" CTA.
 */
export const isCollectComplete = (
  collect: CollectState,
  compose: ComposeState,
): boolean => {
  const steps = collectStepsFor(compose.intent);
  if (steps.includes('recipient_identity')) {
    if (!collect.recipientIdentity?.fullName.trim()) return false;
  }
  if (steps.includes('recipient_payout')) {
    if (!isPayoutDetailsComplete(collect.payoutDetails, compose.route.toCountry))
      return false;
  }
  if (steps.includes('funding_data')) {
    if (!isFundingDetailsComplete(collect.fundingDetails)) return false;
  }
  return true;
};
